import React, { useMemo } from 'react';
import { formatAddressFields } from './utils/shippingSnapshot.js';

const firstNonEmpty = (...values) => values.map((value) => String(value || '').trim()).find(Boolean) || '';

const DCPreviewContent = ({
  dcData,
  linkedOrder,
  selectedFirm,
  sourceWarehouse = null,
  destinationWarehouse = null,
  printRef,
  challanNo,
  typeOfMOC,
  style = pdfContainerStyle,
}) => {
  const totals = useMemo(() => {
    if (!dcData?.components) return { totalPerSet: 0, totalQty: 0 };
    return dcData.components.reduce((acc, component) => {
      acc.totalPerSet += (parseInt(component.perSet || 0, 10) || 0);
      acc.totalQty += (parseInt(component.newQty || 0, 10) + parseInt(component.rentalQty || 0, 10));
      return acc;
    }, { totalPerSet: 0, totalQty: 0 });
  }, [dcData]);

  if (!dcData) return null;

  const resolvedTypeOfMOC = typeOfMOC || dcData.typeOfMOC || '';
  const isInterbranch = resolvedTypeOfMOC === 'Interbranch Transfer';
  const isRental = resolvedTypeOfMOC === 'Rental';
  const termsAndConditionsContent = isRental ? (
    <>
      1. Have received the material in good condition and satisfied with the quality of same.<br />
      2. Have read the scaffold safety notes and also the instructions given by the Nexrise India team for proper use of the scaffold.<br />
      3. Received and carefully read the scaffold safety notes for meticulous compliance at site.<br />
      4. Have verified and cross checked the material quantity. Any post discrepancy or issue for material condition will not be accepted.<br />
      5. Liability in event of any damages, missing or theft of the rented good is sole responsibility of the consignee only.<br />
      6. Rented items remain the property of the rental company (Nexrise India Infra Pvt Ltd.) and that the consignee does not acquire any ownership rights of the same.<br />
      7. In situation of any dispute in regards of any missing and damages or theft of the items we will thereby inform the Consignor within 24 hours of the event and shall be liable to pay any charges claimed the owner of the goods.<br />
      8. We will not transfer the rented items from one project / location to another without prior informing the owner / consignore of the goods.
    </>
  ) : (
    <>
      1. Have received the material in good condition and satisfied with the quality of same.<br />
      2. Have read the scaffold safety notes and also the instructions given by the Nexrise India team for proper use of the scaffold.<br />
      3. Received and carefully read the scaffold safety notes for meticulous compliance at site.<br />
      4. Have verified and cross checked the material quantity. Any post discrepancy or issue for material condition will not be accepted.
    </>
  );
  const referenceNumberLabel = isInterbranch ? 'Transfer No.' : (isRental ? 'WO No.' : 'PO No.');
  const referenceDateLabel = isInterbranch ? 'Transfer Req. Date' : (isRental ? 'WO Date' : 'PO Date');
  const previewChallanNo = challanNo || dcData.challanNo;
  const linkedOrderBillToName = firstNonEmpty(linkedOrder?.customerCompanyName, linkedOrder?.customerName);
  const billToCustomer = isInterbranch
    ? (dcData.billToCustomer || '')
    : firstNonEmpty(dcData.billToCustomer, linkedOrderBillToName, dcData.customerName);
  const shipToCustomer = isInterbranch
    ? (dcData.customerName || destinationWarehouse?.name || dcData.shipToCustomer || '')
    : (dcData.shipToCustomer || dcData.customerName || destinationWarehouse?.name || '');
  const billToAddress = isInterbranch
    ? (dcData.billToAddress || '')
    : firstNonEmpty(dcData.billToAddress, formatAddressFields(linkedOrder?.billingAddress));
  const shipToAddress = dcData.shipToAddress || dcData.shippingAddress || destinationWarehouse?.address || '';
  const projectName = linkedOrder?.projectName || dcData.projectName || '';
  const projectLocation = linkedOrder?.projectLocation?.address || linkedOrder?.projectLocation || dcData.projectLocation || '';
  const destinationGstin = (
    destinationWarehouse?.gstin ||
    destinationWarehouse?.gst_number ||
    destinationWarehouse?.gstNumber ||
    dcData.destinationGstin ||
    dcData.destinationGSTIN ||
    dcData.shipToGstin ||
    dcData.destinationWarehouseGstin ||
    (isInterbranch ? 'URP' : '')
  );
  const billToGstin = isInterbranch
    ? firstNonEmpty(dcData.billToGstin, sourceWarehouse?.gstin, sourceWarehouse?.gst_number) || 'URP'
    : firstNonEmpty(dcData.billToGstin, linkedOrder?.customerGstin, linkedOrder?.billingAddress?.gstin);

  return (
    <div style={style} ref={printRef}>
      <table style={headerTableStyle}>
        <tbody>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '20%', textAlign: 'center', verticalAlign: 'middle' }}>
              {selectedFirm?.logo && <img src={selectedFirm.logo} alt="Logo" style={{ maxWidth: '170px', maxHeight: '92px', objectFit: 'contain' }} />}
            </td>
            <td style={{ ...headerTableCellStyle, width: '50%', fontSize: '16px', lineHeight: '1.35' }}>
              <strong style={{ fontSize: '16px', letterSpacing: '0.2px' }}>{selectedFirm?.name}</strong><br />
              {selectedFirm?.address}<br />
              GSTIN: {selectedFirm?.gstin}<br />
              Email: {selectedFirm?.email} | Tel: {selectedFirm?.tel}
            </td>
            <td style={{ ...headerTableCellStyle, width: '30%', textAlign: 'center', verticalAlign: 'middle' }}>
              <h1 style={{ margin: 0, fontSize: '22px', fontWeight: '900', letterSpacing: '0.4px' }}>DELIVERY CHALLAN</h1>
            </td>
          </tr>
        </tbody>
      </table>

      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr style={{ backgroundColor: '#e2e8f0', fontWeight: 'bold' }}>
            <td style={{ ...sectionHeaderCellStyle, width: '50%' }}>Document Details</td>
            <td style={{ ...sectionHeaderCellStyle, width: '50%' }}>Delivery Details</td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}>Challan#: <strong>{previewChallanNo}</strong></td>
            <td style={headerTableCellStyle}>{referenceNumberLabel}: <strong>{dcData.poNumber}</strong></td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}>Challan Date: <strong>{dcData.dispatchDate}</strong></td>
            <td style={headerTableCellStyle}>{referenceDateLabel}: <strong>{dcData.poDate}</strong></td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}>IRN No.: <strong>{dcData.irnNo || '--'}</strong></td>
            <td style={headerTableCellStyle}>Delivery Mode: <strong>{dcData.deliveryMode} (Type: {dcData.vehicleType || '---'})</strong></td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}>Coordinator: <strong>{dcData.coordinator}</strong></td>
            <td style={headerTableCellStyle}>Dispatched Through: <strong>{dcData.dispatchedThrough}</strong></td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}>Delivery Agent: <strong>{dcData.deliveryAgent || '--'}</strong></td>
            <td style={headerTableCellStyle}>Vehicle Number * : <strong>{dcData.vehicleNumber || '--'}</strong></td>
          </tr>
        </tbody>
      </table>

      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr style={{ backgroundColor: '#e2e8f0', fontWeight: 'bold' }}>
            <td style={{ ...sectionHeaderCellStyle, width: '50%' }}>Bill to</td>
            <td style={{ ...sectionHeaderCellStyle, width: '50%' }}>Ship to</td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}>Customer: <strong>{billToCustomer}</strong></td>
            <td style={headerTableCellStyle}>
              Customer: <strong>{shipToCustomer}</strong>
            </td>
          </tr>
          <tr>
            <td style={{ ...headerTableCellStyle, minHeight: '42px', verticalAlign: 'top' }}>Address: <strong>{billToAddress}</strong></td>
            <td style={{ ...headerTableCellStyle, minHeight: '42px', verticalAlign: 'top' }}>Address: <strong>{shipToAddress}</strong></td>
          </tr>
          <tr>
            <td style={headerTableCellStyle}>GSTIN/UIN: <strong>{billToGstin}</strong></td>
            <td rowSpan={2} style={{ ...headerTableCellStyle, verticalAlign: 'top' }}>
              {isInterbranch ? (
                <div style={{ lineHeight: '1.45' }}>
                  GSTIN: <strong>{destinationGstin || '---'}</strong>
                </div>
              ) : (
                <>
                  <div style={{ lineHeight: '1.45' }}>Project Name: <strong>{projectName || '---'}</strong></div>
                  <div style={{ lineHeight: '1.45' }}>Project Location: <strong>{projectLocation || '---'}</strong></div>
                </>
              )}
            </td>
          </tr>
          <tr><td style={headerTableCellStyle}>Salesperson: <strong>{dcData.salesperson}</strong></td></tr>
          <tr>
            <td style={headerTableCellStyle}>Order By: <strong>{dcData.orderBy}</strong><br />Contact: <strong>{dcData.orderByContact}</strong></td>
            <td style={headerTableCellStyle}>Site Person: <strong>{dcData.sitePerson}</strong><br />Contact: <strong>{dcData.sitePersonContact}</strong></td>
          </tr>
          <tr><td style={headerTableCellStyle}>Email: <strong>{dcData.billToEmail}</strong></td><td style={headerTableCellStyle}>Email: <strong>{dcData.shipToEmail}</strong></td></tr>
        </tbody>
      </table>

      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '33%' }}>Item code: <strong>{dcData.itemCode}</strong></td>
            <td style={{ ...headerTableCellStyle, width: '33%' }}>Tower Height: <strong>{dcData.towerHeight}</strong></td>
            <td style={{ ...headerTableCellStyle, width: '34%' }}>Total Meter: <strong>{dcData.totalMeter}</strong></td>
          </tr>
        </tbody>
      </table>

      {dcData.dcRemarks ? (
        <table style={{ ...headerTableStyle, borderTop: 'none' }}>
          <tbody>
            <tr>
              <td style={{ ...headerTableCellStyle, width: '20%', fontWeight: 'bold', fontSize: '13px' }}>DC Remarks</td>
              <td style={{ ...headerTableCellStyle, fontSize: '16px', lineHeight: '1.5' }}>{dcData.dcRemarks}</td>
            </tr>
          </tbody>
        </table>
      ) : null}

      <div style={{ marginTop: '14px' }}>
        <div style={{ backgroundColor: '#e2e8f0', padding: '8px 10px', border: '1px solid #000', borderBottom: 'none', fontWeight: 'bold', fontSize: '13px', letterSpacing: '0.2px' }}>MATERIAL BREAKDOWN</div>
        <table style={headerTableStyle}>
          <thead>
            <tr style={{ backgroundColor: '#f8fafc' }}>
              <th style={pdfThStyle}>Sr. No.</th>
              <th style={pdfThStyle}>SKU</th>
              <th style={{ ...pdfThStyle, textAlign: 'left' }}>Item</th>
              <th style={pdfThStyle}>Qty Per Set</th>
              <th style={pdfThStyle}>Total Qty</th>
              <th style={{ ...pdfThStyle, width: '22%' }}>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {dcData.components?.map((component, index) => (
              <tr key={index}>
                <td style={{ ...pdfTdStyle, textAlign: 'center' }}>{index + 1}</td>
                <td style={{ ...pdfTdStyle, textAlign: 'center' }}>{component.sku}</td>
                <td style={pdfTdStyle}>{component.name}</td>
                <td style={{ ...pdfTdStyle, textAlign: 'center' }}>{component.perSet}</td>
                <td style={{ ...pdfTdStyle, textAlign: 'center', fontWeight: 'bold' }}>{parseInt(component.newQty || 0, 10) + parseInt(component.rentalQty || 0, 10)}</td>
                <td style={{ ...pdfTdStyle, textAlign: 'left' }}>{component.remarks || ''}</td>
              </tr>
            ))}
            <tr style={{ backgroundColor: '#f8fafc', fontWeight: 'bold' }}>
              <td colSpan="3" style={{ ...pdfTdStyle, textAlign: 'right', paddingRight: '10px' }}>TOTAL QUANTITY:</td>
              <td style={{ ...pdfTdStyle, textAlign: 'center' }}>{totals.totalPerSet}</td>
              <td style={{ ...pdfTdStyle, textAlign: 'center' }}>{totals.totalQty}</td>
              <td style={{ ...pdfTdStyle, textAlign: 'left' }}></td>
            </tr>
          </tbody>
        </table>
      </div>

      <table style={{ ...headerTableStyle, borderTop: 'none', marginTop: '14px' }}>
        <tbody>
          <tr style={{ backgroundColor: '#e2e8f0', fontWeight: 'bold' }}><td style={{ ...sectionHeaderCellStyle, fontSize: '13px' }}>Terms and Conditions</td></tr>
          <tr>
            <td style={{ ...headerTableCellStyle, fontSize: '16px', lineHeight: '1.55', padding: '9px 10px' }}>
              {termsAndConditionsContent}
            </td>
          </tr>
        </tbody>
      </table>

      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '50%' }}>
              Received By (Name): <strong>{dcData.receivedByName || '____________________'}</strong><br /><br />
              Contact Number: <strong>{dcData.receivedByContact || '____________________'}</strong><br /><br />
              Date & Time of Handover: <strong>{dcData.handoverDateTime || '____________________'}</strong>
            </td>
            <td style={{ ...headerTableCellStyle, width: '50%', textAlign: 'center', verticalAlign: 'bottom', height: '96px' }}>
              <strong>Customer seal and Signature</strong>
            </td>
          </tr>
        </tbody>
      </table>

      <table style={{ ...headerTableStyle, borderTop: 'none' }}>
        <tbody>
          <tr style={{ backgroundColor: '#e2e8f0', fontWeight: 'bold' }}>
            <td colSpan="5" style={{ ...sectionHeaderCellStyle, textAlign: 'center' }}>Internal Purpose Only</td>
          </tr>
          <tr>
            <td style={{ ...headerTableCellStyle, width: '20%', verticalAlign: 'center' }}><strong>Verified and Checked By:</strong></td>
            <td rowSpan="3" style={{ ...headerTableCellStyle, width: '20%' }}></td>
            <td rowSpan="3" style={{ ...headerTableCellStyle, width: '20%', textAlign: 'center', verticalAlign: 'bottom', paddingBottom: '10px' }}>
              <strong>Seal & Sign (Consignor)</strong>
            </td>
            <td rowSpan="3" style={{ ...headerTableCellStyle, width: '20%', textAlign: 'center', verticalAlign: 'top' }}>
              <strong>Prepared By:</strong><br />
              <div style={{ height: '88px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src="/Rajesh-Kumar-Sign.png" alt="Sign" style={{ maxHeight: '78px', objectFit: 'contain' }} />
              </div>
              Rajesh Kumar
            </td>
            <td rowSpan="3" style={{ ...headerTableCellStyle, width: '20%', textAlign: 'center', verticalAlign: 'top' }}>
              <strong>Approved By:</strong><br />
              <div style={{ height: '88px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src="/Ghansyam-Sign.png" alt="Sign" style={{ maxHeight: '78px', objectFit: 'contain' }} />
              </div>
              Ghanshyam Prajapt
            </td>
          </tr>
          <tr><td style={{ ...headerTableCellStyle, verticalAlign: 'center' }}><strong>Time & date:</strong></td></tr>
          <tr><td style={{ ...headerTableCellStyle, verticalAlign: 'center' }}><strong>Sign:</strong></td></tr>
        </tbody>
      </table>

      {(dcData.taxInvoiceUrl || dcData.ewayBillUrl || dcData.einvoiceUrl) && (
        <div style={{ marginTop: '10px', fontSize: '12px', color: '#475569', borderTop: '1px solid #cbd5e1', paddingTop: '6px', lineHeight: '1.35' }}>
          <strong>Attached Docs:</strong>
          {dcData.taxInvoiceUrl && <span style={{ marginLeft: '10px' }}>Tax Invoice Attached OK</span>}
          {dcData.ewayBillUrl && <span style={{ marginLeft: '10px' }}>E-way Bill Attached OK</span>}
          {dcData.einvoiceUrl && <span style={{ marginLeft: '10px' }}>E-Invoice Attached OK</span>}
        </div>
      )}
    </div>
  );
};

const pdfContainerStyle = { backgroundColor: 'white', padding: '22px', border: '1px solid #e2e8f0', minHeight: '1000px', color: '#000', fontFamily: '"Segoe UI", Arial, sans-serif' };
const headerTableStyle = { width: '100%', borderCollapse: 'collapse', border: '1px solid #000' };
const headerTableCellStyle = { border: '1px solid #000', padding: '6px 8px', fontSize: '16px', textAlign: 'left', lineHeight: '1.35', verticalAlign: 'top' };
const sectionHeaderCellStyle = { ...headerTableCellStyle, fontSize: '16px', fontWeight: '700', letterSpacing: '0.2px' };
const pdfThStyle = { border: '1px solid #000', padding: '6px 8px', fontSize: '16px', textAlign: 'center', fontWeight: 'bold', lineHeight: '1.3' };
const pdfTdStyle = { border: '1px solid #000', padding: '6px 8px', fontSize: '16px', textAlign: 'left', lineHeight: '1.35', verticalAlign: 'top' };

export default DCPreviewContent;
